import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Dashboard from './components/Dashboard';
import LoginPage from './components/LoginPage';
import { ToastProvider, Sandbox } from './components/bpm';
import { AuthProvider, useAuth } from './contexts/AuthContext';
// PHASE 2: Feature flag pour basculer entre PortfolioContext et PortfolioContextV2
import { PortfolioProvider } from './contexts/PortfolioContextIndex';
import { TabRegistryProvider } from './contexts/TabRegistryContext';
import { NotificationHistoryProvider } from './contexts/NotificationHistoryContext';
import { OliverProvider } from './contexts/OliverContext';
import { queryClient } from './config/queryClient';
import './ai/loadContexts';
import './ai/loadAliases';
import './styles/bpm.theme.css';
import './App.css';

// Remplacez par votre Client ID Google OAuth
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';

// Composant interne pour gérer l'affichage conditionnel avec transition fade
const AppContent = () => {
  const { isAuthenticated, sessionCheckDone } = useAuth();
  const [showLogin, setShowLogin] = React.useState(true);
  const [showDashboard, setShowDashboard] = React.useState(false);
  const sessionCheckDoneOnce = React.useRef(false);

  React.useEffect(() => {
    if (!sessionCheckDone) return;
    // Premier passage après validation de session : afficher directement la bonne page (sans transition)
    if (!sessionCheckDoneOnce.current) {
      sessionCheckDoneOnce.current = true;
      setShowLogin(!isAuthenticated);
      setShowDashboard(isAuthenticated);
      return;
    }
    // Connexion / déconnexion : transition
    if (isAuthenticated) {
      setShowLogin(false);
      const timer = setTimeout(() => setShowDashboard(true), 250);
      return () => clearTimeout(timer);
    } else {
      setShowDashboard(false);
      const timer = setTimeout(() => setShowLogin(true), 250);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, sessionCheckDone]);

  // Tant que la session n'a pas été validée au chargement (token vérifié), afficher un chargement
  if (!sessionCheckDone) {
    return (
      <div className="app-transition-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-primary, #f5f5f5)' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-primary, #333)' }}>Chargement…</div>
      </div>
    );
  }

  return (
    <div className="app-transition-container">
      {showLogin && (
        <div className={`page-transition ${isAuthenticated ? 'fade-out' : 'fade-in'}`}>
          <LoginPage />
        </div>
      )}
      {showDashboard && (
        <div className={`page-transition ${isAuthenticated ? 'fade-in' : 'fade-out'}`}>
          <Dashboard />
        </div>
      )}
    </div>
  );
};

function App() {
  // Appliquer le thème et la couleur d'accent au chargement de l'application
  React.useEffect(() => {
    try {
      const darkTheme = localStorage.getItem('darkTheme') === 'true';
      if (darkTheme) {
        document.documentElement.classList.add('theme-dark');
      } else {
        document.documentElement.classList.remove('theme-dark');
      }
      // Meta theme-color pour status bar / navigation bar sur mobile (évite zones blanches)
      const metaTheme = document.querySelector('meta[name="theme-color"]');
      if (metaTheme) {
        metaTheme.setAttribute('content', darkTheme ? '#1a1a1a' : '#ffffff');
      }
      const accentColor = localStorage.getItem('accentColor');
      if (accentColor && /^#[0-9A-Fa-f]{6}$/.test(accentColor)) {
        document.documentElement.style.setProperty('--accent-color', accentColor);
      } else {
        document.documentElement.style.setProperty('--accent-color', '#A49B6D');
      }
    } catch (e) {
      // Ignorer les erreurs de localStorage
    }
  }, []);

  // Route sandbox BPM : /sandbox?component=bpm.panel&variant=warning ou ?sandbox=1&component=... (sans auth, pour iframe doc)
  const isSandbox = typeof window !== 'undefined' && (
    window.location.pathname === '/sandbox' ||
    (window.location.pathname === '/' && new URLSearchParams(window.location.search).get('sandbox') === '1')
  );

  if (isSandbox) {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="App">
          <Sandbox />
        </div>
      </QueryClientProvider>
    );
  }

  // Vérifier si le Client ID est configuré
  if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'YOUR_GOOGLE_CLIENT_ID') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2>⚠️ Configuration Google OAuth manquante</h2>
        <p>Le Client ID Google OAuth n'est pas configuré.</p>
        <p style={{ marginTop: '20px' }}>
          <strong>Instructions :</strong>
        </p>
        <ol style={{ textAlign: 'left', maxWidth: '600px', marginTop: '10px' }}>
          <li>Créez un fichier <code>.env</code> dans le dossier <code>frontend/</code></li>
          <li>Ajoutez la ligne : <code>REACT_APP_GOOGLE_CLIENT_ID=votre-client-id</code></li>
          <li>Obtenez votre Client ID depuis <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
          <li>Redémarrez le serveur de développement</li>
        </ol>
        <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
          Consultez <code>frontend/GOOGLE_OAUTH_SETUP.md</code> pour plus de détails.
        </p>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <TabRegistryProvider>
            <NotificationHistoryProvider>
              <OliverProvider>
                <ToastProvider>
                  <PortfolioProvider>
                    <div className="App">
                      <AppContent />
                    </div>
                  </PortfolioProvider>
                </ToastProvider>
              </OliverProvider>
            </NotificationHistoryProvider>
          </TabRegistryProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
      {/* DevTools React Query (uniquement en développement) */}
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;